import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import EntityLogo from "@/components/EntityLogo";
import api from "@/lib/api";
import { Building2, Search, MapPin, BadgeCheck, Star } from "lucide-react";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export default function OrganizationsDirectory() {
  const [orgs, setOrgs] = useState(null);
  const [err, setErr] = useState(false);
  const [q, setQ] = useState("");
  const [prov, setProv] = useState("");

  const load = () => {
    setErr(false); setOrgs(null);
    api.get("/live/organizations").then(({ data }) => setOrgs(data || [])).catch(() => { setErr(true); setOrgs([]); });
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!orgs) return [];
    const query = q.trim().toLowerCase();
    return orgs.filter((o) =>
      (!prov || o.province === prov) &&
      (!query || `${o.name} ${o.city || ""} ${o.province || ""}`.toLowerCase().includes(query))
    );
  }, [orgs, q, prov]);

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] py-12" id="search">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="label-overline text-white/50">Clubs &amp; Teams</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter text-white mt-2">Organization Directory</h1>
          <p className="text-white/60 mt-3 max-w-2xl">Discover Canadian baseball organizations, academies and associations on CBR. Search by name or province.</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="org-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organizations…"
                className="w-full bg-white/10 border border-white/15 text-white placeholder-white/40 rounded-sm pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#E63946]" />
            </div>
            <select data-testid="org-province-filter" value={prov} onChange={(e) => setProv(e.target.value)}
              className="bg-white/10 border border-white/15 text-white rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-[#E63946]">
              <option value="" className="text-black">All Provinces</option>
              {PROVINCES.map((p) => <option key={p} value={p} className="text-black">{p}</option>)}
            </select>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10 min-h-[40vh]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {orgs === null ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="org-loading">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-sm animate-pulse" />)}
            </div>
          ) : err ? (
            <div className="text-center py-20" data-testid="org-error">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">We couldn't load organizations right now.</p>
              <button onClick={load} className="mt-3 text-sm font-semibold text-[#E63946] uppercase tracking-wider">Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="org-empty">
              <Building2 className="w-10 h-10 text-slate-300 mx-auto" />
              <p className="text-slate-500 mt-3 text-sm">No organizations match your search yet.</p>
            </div>
          ) : (
            <>
              <p className="text-xs text-slate-500 mb-4" data-testid="org-count">{filtered.length} organization{filtered.length !== 1 ? "s" : ""}</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="org-grid">
                {filtered.map((o) => (
                  <Link key={o.id} to={`/live/orgs/${o.slug}`} data-testid={`org-card-${o.slug}`}
                    className="group bg-white border border-slate-200 rounded-sm p-4 flex items-center gap-4 hover:border-[#E63946] hover:shadow-sm transition-all">
                    <EntityLogo src={o.logo_url} alt={o.name} size="w-14 h-14" textSize="text-sm" />
                    <div className="min-w-0">
                      <div className="font-display text-lg uppercase tracking-tight text-[#0A192F] truncate group-hover:text-[#E63946]">{o.name}</div>
                      <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                        {(o.city || o.province) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[o.city, o.province].filter(Boolean).join(", ")}</span>}
                        {o.type && <span className="capitalize">· {o.type.replace(/_/g, " ")}</span>}
                      </div>
                      <div className="flex items-center gap-1.5 mt-1.5">
                        {o.verified && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded-sm"><BadgeCheck className="w-3 h-3" /> Verified</span>}
                        {o.founding_member && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-[#E63946]/10 text-[#E63946] px-1.5 py-0.5 rounded-sm"><Star className="w-3 h-3" /> Founding</span>}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
